import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { refreshToken } from '../../../../Redux/Auth/authActions';
import { partnerPortalApi } from '../Services/partnerPortalApi';
import { resolveAccessToken, usePartnerAccessToken } from './usePartnerAccessToken';

export const PSM_PROJECT_ACTIONS = {
  APPROVE: 'approve',
  REQUEST_INFO: 'request_info',
  UPDATE_STATUS: 'update_status',
  ADD_NOTE: 'add_note',
};

const isUnauthorizedError = (error) => {
  const message = (error?.message || '').toLowerCase();
  return message.includes('unauthorized') || message.includes('authentication');
};

const APPROVAL_ACTIONS = new Set(['approve', 'approved']);
const INFO_REQUEST_ACTIONS = new Set(['request_info', 'info_requested']);

const actionsMatchForEnrichment = (itemAction, targetAction) => {
  const item = String(itemAction || '').toLowerCase();
  const target = String(targetAction || '').toLowerCase();
  if (item === target) {
    return true;
  }
  if (APPROVAL_ACTIONS.has(item) && APPROVAL_ACTIONS.has(target)) {
    return true;
  }
  if (INFO_REQUEST_ACTIONS.has(item) && INFO_REQUEST_ACTIONS.has(target)) {
    return true;
  }
  return false;
};

const entryHasNote = (item) => Boolean(
  item?.psmNote || item?.note || item?.message || item?.text,
);

const enrichActivityLogWithNote = (activityLog, action, note) => {
  if (!action || !note || !Array.isArray(activityLog) || activityLog.length === 0) {
    return activityLog;
  }

  const matchingIndex = [...activityLog]
    .map((item, index) => ({ item, index }))
    .reverse()
    .find(({ item }) => actionsMatchForEnrichment(item.action, action))?.index;

  if (matchingIndex === undefined) {
    return activityLog;
  }

  const entry = activityLog[matchingIndex];
  if (entryHasNote(entry)) {
    return activityLog;
  }

  return activityLog.map((item, index) => (
    index === matchingIndex
      ? { ...item, psmNote: note, note }
      : item
  ));
};

export const mergePSMActionResult = (project, result, payload = {}) => {
  if (!project) {
    return project;
  }

  const action = payload?.action;
  const note = String(payload?.note || '').trim();
  const baseActivityLog = result?.activityLog ?? project.activityLog;
  const activityLog = enrichActivityLogWithNote(baseActivityLog, action, note);
  const resolvedProjectStatus = result?.projectStatus
    ?? (action === PSM_PROJECT_ACTIONS.UPDATE_STATUS ? payload?.projectStatus : undefined)
    ?? project.projectStatus;

  return {
    ...project,
    ...result,
    projectStatus: resolvedProjectStatus,
    paeApproved: action === PSM_PROJECT_ACTIONS.APPROVE
      ? true
      : (result?.paeApproved ?? project.paeApproved),
    statusTimeline: result?.statusTimeline ?? project.statusTimeline,
    activityLog,
  };
};

export const usePSMProjectActions = () => {
  const dispatch = useDispatch();
  const accessToken = usePartnerAccessToken();
  const [submitting, setSubmitting] = useState(false);

  const withAuthRetry = useCallback(async (apiCall) => {
    const token = resolveAccessToken(accessToken);
    if (!token) {
      throw new Error('Your session has expired. Please sign in again.');
    }

    try {
      return await apiCall(token);
    } catch (err) {
      if (!isUnauthorizedError(err) || !Cookies.get('refreshToken')) {
        throw err;
      }

      await dispatch(refreshToken());
      const refreshedToken = Cookies.get('accessToken');
      if (!refreshedToken) {
        throw new Error('Your session has expired. Please sign in again.');
      }

      return apiCall(refreshedToken);
    }
  }, [accessToken, dispatch]);

  const runAction = useCallback(async (projectId, payload) => {
    setSubmitting(true);

    try {
      const result = await withAuthRetry((token) => (
        partnerPortalApi.postPAEProjectAction(token, projectId, payload)
      ));
      return result;
    } finally {
      setSubmitting(false);
    }
  }, [withAuthRetry]);

  return {
    submitting,
    runAction,
  };
};
